import "./App.css";

import {
  useMemo,
  useState,
  useEffect,
  useRef,
  memo,
  useCallback,
  Fragment,
} from "react";

import Typography from "@mui/material/Typography";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";

import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MailIcon from "@mui/icons-material/Mail";
import ReplayIcon from "@mui/icons-material/Replay";
import ForumIcon from "@mui/icons-material/Forum";
import Checkbox from "@mui/material/Checkbox";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Badge from "@mui/material/Badge";
import TextField from "@mui/material/TextField";

import InputAdornment from "@mui/material/InputAdornment";
import LinkIcon from "@mui/icons-material/Link";
import Tooltip from "@mui/material/Tooltip";
import Stack from "@mui/material/Stack";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import PushPinIcon from "@mui/icons-material/PushPin";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import { Icon } from "@iconify/react";

import DeleteIcon from "@mui/icons-material/Delete";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsightsIcon from "@mui/icons-material/Insights";
import SettingsIcon from "@mui/icons-material/Settings";
import AutoAwesomeMosaicIcon from "@mui/icons-material/AutoAwesomeMosaic";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddIcon from "@mui/icons-material/Add";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";

import { Authenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";

import TourIcon from "@mui/icons-material/Tour";
import Button from "@mui/material/Button";
import ProjectPicker from "./ProjectPicker";

import {
  BrowserRouter as Router,
  Route,
  Routes,
  useParams,
  useLocation,
} from "react-router-dom";

import EditIcon from "@mui/icons-material/Edit";
import FavoriteIcon from "@mui/icons-material/Favorite";

import PhotoIcon from "@mui/icons-material/Photo";
import CropOriginalIcon from "@mui/icons-material/CropOriginal";
import { MuiChipsInput } from "mui-chips-input";
import Collapse from "@mui/material/Collapse";

import TurnedInIcon from "@mui/icons-material/TurnedIn";
import TurnedInNotIcon from "@mui/icons-material/TurnedInNot";
import ShareIcon from "@mui/icons-material/Share";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import init from "jsriki";

import {
  RecoilRoot,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from "recoil";

import store from "store2";
import PestControlIcon from "@mui/icons-material/PestControl";
import {
  http_post,
  hookUrl,
  switchStage,
  activeProject,
  getActiveProject,
  changeActiveProject,
} from "./Auth";

import {
  imageToggleState,
  storyFilterBoxState,
  projectPickerState,
  debugToggleState,
  activeLabelsState,
  captionsState,
  peopleDataState,
  timeFilterState,
  labelToggleState,
  newStoriesState,
  mergedStoriesState,
  joinStoriesState,
  searchState,
  reloadState,
  negateLabelsState,
  showSelectedStoriesState,
} from "./RecoilStates";

import * as lodash from "lodash";

import GraphView from "./StoryGraph";
import { getTimestamp } from "./MessagePreview";

import { ButtonLink } from "./Image";

import { WebStory } from "./WebStory";
import { StoryLabels } from "./StoryLabels";
import { Search } from "./Search";
import { StoryLabelFilterBox, LabelsData } from "./StoryLabelFilterBox";
import { StoryCaption } from "./StoryCaption";
import { StoryDetails } from "./StoryDetails";
import { Thread, MemoMessage, exitToApp } from "./Thread";
import log from "./logger";

import * as u from "./utility";
import PouchDB from "pouchdb-browser";

import { DEFAULT_FONT_SIZE } from "./constants";

const drawerWidth = 169;

function RetryToast({ message, onRetry, onUndo }) {
  return (
    <Stack direction={"row"} alignItems={"center"}>
      <Typography fontSize={12}>{message}</Typography>
      {onRetry && (
        <Button onClick={onRetry} size="small">
          RETRY
        </Button>
      )}
      {onUndo && (
        <Button onClick={onUndo} size="small">
          UNDO
        </Button>
      )}
    </Stack>
  );
}

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(1),
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    ...(open && {
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
  })
);

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

const getCheckedThreads = (selected) => {
  let threads = [];
  for (let [k, v] of Object.entries(selected)) {
    if (v) {
      threads.push(k);
    }
  }
  return threads;
};

function ShowSelectedStoriesButton() {
  const [selected, setSelected] = useRecoilState(joinStoriesState);
  const [numSelected, setNumSelected] = useState(0);
  const setShowSelectedStories = useSetRecoilState(showSelectedStoriesState);

  useEffect(() => {
    setNumSelected(getCheckedThreads(selected).length);
  }, [selected]);

  const handleClick = () => {
    setShowSelectedStories(true);
  };

  const handleClear = () => {
    setSelected({});
  };

  return (
    <>
      <IconButton
        color="inherit"
        edge="start"
        sx={{ mr: 2 }}
        disabled={numSelected === 0}
        onClick={handleClick}
      >
        <CheckBoxIcon />
      </IconButton>
      <IconButton
        color="inherit"
        edge="start"
        sx={{ mr: 2 }}
        disabled={numSelected === 0}
        onClick={handleClear}
      >
        <DisabledByDefaultRoundedIcon />
      </IconButton>
    </>
  );
}

const JoinButton = ({ onJoin, onUndo, sx }) => {
  const [selected, setSelected] = useRecoilState(joinStoriesState);
  const [numSelected, setNumSelected] = useState(0);
  const project = useRecoilValue(projectPickerState);

  useEffect(() => {
    setNumSelected(getCheckedThreads(selected).length);
  }, [selected]);

  useEffect(() => {
    setSelected({});
  }, [project, setSelected]);

  const handleJoinThreads = () => {
    const threads = getCheckedThreads(selected);
    if (!threads.length) {
      return;
    }

    const disjoinThreads = async (story, threads) => {
      if (threads.length > 0) {
        try {
          await http_post("label", {
            disjoin: { threads: threads, story: story },
          });
          if (onUndo) {
            onUndo();
          }
        } catch (err) {}
      }
    };

    const joinThreads = async (stories) => {
      const attemptJoin = async (stories) => {
        try {
          const response = await http_post("label", {
            join: { stories: stories },
          });
          const joined = response.data.join;

          if (joined.length > 0) {
            const undo = () => {
              disjoinThreads(joined[0], joined.slice(1));
            };

            if (joined.length !== stories.length) {
              let left = [joined[0]];
              for (let s of stories) {
                if (!joined.includes(s)) {
                  left.push(s);
                }
              }

              const message = `We could not merge ${left.length} stories. Perhaps, it's too much work to do in one go.`;
              const retry = () => attemptJoin(left);

              if (joined.length > 1) {
                toast.warning(
                  <RetryToast message={message} onRetry={retry} onUndo={undo} />
                );
              } else {
                toast.warning(<RetryToast message={message} onRetry={retry} />);
              }
            } else {
              toast.success(
                <RetryToast
                  message={`Successfully joined ${stories.length} stories!`}
                  onUndo={undo}
                />
              );
            }

            onJoin(joined);
          }
        } catch (err) {}
      };

      await attemptJoin(stories);
      setSelected({});
    };

    joinThreads(threads);
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleJoinThreads}
        edge="start"
        disabled={numSelected < 2}
        sx={{ mr: 2, ...sx }}
      >
        <>
          {numSelected >= 2 && (
            <Badge color="error" badgeContent={numSelected.toString()}>
              <ForumIcon />
            </Badge>
          )}
          {numSelected === 1 && (
            <Badge badgeContent={numSelected.toString()}>
              <ForumIcon />
            </Badge>
          )}
          {numSelected === 0 && <ForumIcon />}
        </>
      </IconButton>
    </>
  );
};

const BulkDelete = ({ onDelete, onUndo, sx }) => {
  const [selected, setSelected] = useRecoilState(joinStoriesState);
  const [numSelected, setNumSelected] = useState(0);
  const project = useRecoilValue(projectPickerState);

  useEffect(() => {
    setNumSelected(getCheckedThreads(selected).length);
  }, [selected]);

  useEffect(() => {
    setSelected({});
  }, [project, setSelected]);

  const handleDelete = () => {
    const threads = getCheckedThreads(selected);
    if (!threads.length) {
      return;
    }

    const deleteBulk = async (stories) => {
      const restoreBulk = async (stories) => {
        try {
          const response = await http_post("label", {
            restore: { stories: stories },
          });
          const restored = response.data.restore;
          if (restored.length > 0) {
            setSelected((prev) => {
              let next = prev ? { ...prev } : {};
              for (let d of restored) {
                next[d] = true;
              }
              return next;
            });
          }
          if (onUndo) {
            onUndo();
          }
        } catch (err) {}
      };

      try {
        const response = await http_post("label", {
          delete: { stories: stories },
        });
        const deleted = response.data.delete;
        if (deleted.length > 0) {
          setSelected((prev) => {
            let res = prev ? { ...prev } : {};
            for (let d of deleted) {
              delete res[d];
            }
            return res;
          });

          let message = "Successfully deleted ";
          if (deleted.length > 1) {
            message += `${deleted.length} stories.`;
          } else {
            message += "1 story.";
          }

          if (onDelete) {
            onDelete(deleted);
          }

          toast.success(
            <RetryToast message={message} onUndo={() => restoreBulk(deleted)} />
          );
        }
      } catch (err) {}
    };

    deleteBulk(threads);
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleDelete}
        edge="start"
        disabled={numSelected < 1}
        sx={{ mr: 2, ...sx }}
      >
        <>
          {numSelected > 0 && (
            <Badge color="error" badgeContent={numSelected.toString()}>
              <DeleteIcon />
            </Badge>
          )}
          {numSelected === 0 && <DeleteIcon />}
        </>
      </IconButton>
    </>
  );
};

function ImageToggle() {
  const [toggle, setToggle] = useRecoilState(imageToggleState);

  const handleClick = () => {
    setToggle(!toggle);
    u.writeLocalStorage("ImageToggle", toggle ? "false" : "true");
  };

  return (
    <Tooltip title={toggle ? "Hide pictures" : "Show pictures"}>
      <IconButton
        color="inherit"
        onClick={handleClick}
        edge="start"
        sx={{ mr: 2 }}
      >
        {toggle && <CropOriginalIcon />}
        {!toggle && <PhotoIcon />}
      </IconButton>
    </Tooltip>
  );
}

function DebugToggle() {
  const [toggle, setToggle] = useRecoilState(debugToggleState);

  const handleClick = () => {
    setToggle(!toggle);
  };

  return (
    <Tooltip title={toggle ? "Debug mode on" : "Debug mode off"}>
      <IconButton
        color="inherit"
        onClick={handleClick}
        edge="start"
        sx={{ mr: 2 }}
      >
        <PestControlIcon />
      </IconButton>
    </Tooltip>
  );
}

function LabelToggle() {
  const [toggle, setToggle] = useRecoilState(labelToggleState);

  const handleClick = () => {
    setToggle(!toggle);
    u.writeLocalStorage("LabelToggle", toggle ? "false" : "true");
  };

  return (
    <Tooltip title={toggle ? "Hide labels" : "Show labels"}>
      <IconButton
        color="inherit"
        onClick={handleClick}
        edge="start"
        sx={{ mr: 2 }}
      >
        {toggle && <TurnedInNotIcon />}
        {!toggle && <TurnedInIcon />}
      </IconButton>
    </Tooltip>
  );
}

function getCategoryToShow(type) {
  if (type === "story") {
    return "Stories";
  } else if (type === "message") {
    return "Artifacts";
  } else {
    return u.readLocalStorage("Home", "Stories");
  }
}

function Home({ signOut }) {
  const theme = useTheme();
  const [open, setOpen] = useState(u.readLocalStorage("AppBarOpened", true));
  const { type, proj, id, ts } = useParams();
  const topicFilter = type === "story" ? id : null;
  const messageFilter = type === "message" ? id : null;
  const projectFilter = type === "project" || proj ? proj : null;
  const [selected, setSelected] = useState(getCategoryToShow(type));
  const [newLabel, setNewLabel] = useState();
  const [collapseAll, setCollapseAll] = useState(0);
  const [project, setProject] = useRecoilState(projectPickerState);
  const setReload = useSetRecoilState(reloadState);
  const setMergedStories = useSetRecoilState(mergedStoriesState);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleDataReload = () => {
    setReload((prev) => prev + 1);
  };

  const handleStoriesJoined = (stories) => {
    setMergedStories(stories);
  };

  const handleCollapseAll = () => {
    setCollapseAll(collapseAll + 1);
  };

  const handleSignOut = () => {
    signOut();
  };

  const handleLabelCreated = useCallback(
    (newLabel) => {
      setNewLabel(newLabel);
    },
    [setNewLabel]
  );

  const handleSelectCategory = (cat) => {
    if (messageFilter || topicFilter) {
      const url = u.getProjectUrl(project);
      window.location.href = url;

      // see u.writeLocalStorage
      const qualifiedKey = `Home:/${project}${window.location.search}`;
      store(qualifiedKey, cat);
    } else if (!type) {
      setSelected(cat);
      u.writeLocalStorage("Home", cat);
    }
  };

  const handleLogoClicked = () => {
    const url = project ? u.getProjectUrl(project) : u.getHomeUrl();
    if (window.location.href !== url) {
      window.location.href = url;
    } else {
      handleDataReload();
    }
  };

  useEffect(() => {
    let newProject = project;
    if (topicFilter || messageFilter || projectFilter) {
      newProject = proj;
    }

    if (activeProject !== newProject) {
      changeActiveProject(newProject);
      if (project !== newProject) {
        setProject(newProject);
      }
    }

    document.title = `topcat | ${project}`;
  }, [project]);

  useEffect(() => {
    u.writeLocalStorage("AppBarOpened", open);
  }, [open]);

  const showFilters = () => {
    return (
      selected === "Stories" ||
      selected === "Popular" ||
      selected === "Recent" ||
      selected === "Graph"
    );
  };

  const showStoryControls = () => {
    return (
      selected === "Stories" ||
      selected === "Popular" ||
      selected === "Recent" ||
      selected === "Events"
    );
  };

  const showTimeFilters = () => {
    return (
      selected === "Stories" ||
      selected === "Popular" ||
      selected === "Recent" ||
      selected === "Artifacts" ||
      selected === "Graph"
    );
  };

  const handleStoryClicked = (storyId) => {
    const url = u.getStoryUrlFromId(storyId, activeProject);
    window.open(url, "_blank", "noreferrer");
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar position="fixed" open={open}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{ mr: 2, ...(open && { display: "none" }) }}
          >
            <MenuIcon />
          </IconButton>

          <IconButton
            color="inherit"
            onClick={handleDrawerClose}
            edge="start"
            sx={{ mr: 2, ...(!open && { display: "none" }) }}
          >
            {theme.direction === "ltr" ? (
              <ChevronLeftIcon />
            ) : (
              <ChevronRightIcon />
            )}
          </IconButton>

          <Box
            component="img"
            sx={{
              height: 40,
              mr: 3,
              ml: 0,
              mb: 1,
              mt: 1,
              ...(open && { display: "none" }),
            }}
            src="https://stumti-webapp.s3.eu-west-1.amazonaws.com/resources/logo.png"
            onClick={handleLogoClicked}
          />

          {(showStoryControls() ||
            selected === "Graph" ||
            selected === "Artifacts") && (
            <IconButton
              color="inherit"
              onClick={handleDataReload}
              edge="start"
              sx={{ mr: 2 }}
            >
              <ReplayIcon />
            </IconButton>
          )}

          {showStoryControls() && (
            <>
              <ShowSelectedStoriesButton />
              <IconButton
                color="inherit"
                onClick={handleCollapseAll}
                edge="start"
                sx={{ mr: 2 }}
              >
                <ExpandLessIcon />
              </IconButton>

              <ImageToggle />
            </>
          )}

          {(showStoryControls() || selected === "Artifacts") && <DebugToggle />}

          {showStoryControls() && <LabelToggle />}

          {!topicFilter && !messageFilter && (
            <Box width="20%">
              <ProjectPicker />
            </Box>
          )}
          {showStoryControls() && (
            <>
              <JoinButton
                onJoin={handleStoriesJoined}
                onUndo={handleDataReload}
                sx={{ ml: 1 }}
              />
              <BulkDelete
                onDelete={handleDataReload}
                onUndo={handleDataReload}
              />
            </>
          )}
          <Button
            variant="contained"
            sx={{ ml: "auto" }}
            onClick={handleSignOut}
          >
            Sign out
          </Button>
        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        <DrawerHeader>
          <Box
            component="img"
            sx={{ height: 40, mr: 3, ml: 0, mb: 1, mt: 1 }}
            src="https://stumti-webapp.s3.eu-west-1.amazonaws.com/resources/logo.png"
            onClick={handleLogoClicked}
          />
        </DrawerHeader>
        <Divider />
        <SideMenu selected={selected} setSelected={handleSelectCategory} />
        {showTimeFilters() && !topicFilter && !messageFilter && (
          <>
            <Divider />
            <TimeFilters />
          </>
        )}

        <LabelsData />
        {showFilters() && (
          <>
            <Divider />
            <Box
              sx={{
                pr: 2,
                display: topicFilter || messageFilter ? "none" : undefined,
              }}
            >
              <StoryLabelFilterBox newLabel={newLabel} />
            </Box>
          </>
        )}
        <PeopleData />
      </Drawer>
      <Main open={open}>
        <DrawerHeader />
        <ToastContainer />
        {selected === "Stories" && (
          <>
            {true && (
              <Stack
                direction="row"
                sx={{ width: "100%" }}
                justifyContent="flex-end"
              >
                <Box width="100%">
                  <WebStory />
                </Box>
                <Search />
              </Stack>
            )}
            <FetchStories
              collapseAll={collapseAll}
              filterId={topicFilter}
              timestampRef={ts}
              onLabelCreated={handleLabelCreated}
            />
          </>
        )}
        {selected === "Artifacts" && <RawMessages filterId={messageFilter} />}
        {selected === "Deleted" && (
          <MemoFetchStories
            collapseAll={collapseAll}
            filterId={topicFilter}
            timestampRef={ts}
            deleted={true}
          />
        )}
        {selected === "Popular" && (
          <MemoFetchStories
            collapseAll={collapseAll}
            filterId={topicFilter}
            timestampRef={ts}
            popular={true}
            onLabelCreated={handleLabelCreated}
          />
        )}
        {selected === "Recent" && (
          <MemoFetchStories
            collapseAll={collapseAll}
            filterId={topicFilter}
            timestampRef={ts}
            recent={true}
            onLabelCreated={handleLabelCreated}
          />
        )}
        {selected === "Events" && (
          <MemoFetchStories
            collapseAll={collapseAll}
            filterId={topicFilter}
            timestampRef={ts}
            events={true}
            onLabelCreated={handleLabelCreated}
          />
        )}
        {selected === "Graph" && (
          <Box sx={{ height: "100%", width: "100%" }}>
            <GraphView onStoryClicked={handleStoryClicked} />
          </Box>
        )}
        {selected === "Vitals" && (
          <Typography>
            - team analytics
            <br />
            - team dynamics
            <br />
            - who's talking to whom <br />
            - activity charts across channels <br />
            - app statistics and activity (e.g. how many messages in slack
            daily) <br />
          </Typography>
        )}
        {selected === "Settings" && <Settings />}
        {selected === "People" && <People />}
      </Main>
    </Box>
  );
}

function SideMenu({ selected, setSelected }) {
  const [todayEvents, setTodayEvents] = useState(null);
  const project = useRecoilValue(projectPickerState);
  const [reloadData] = useRecoilState(reloadState);

  const icons = {
    Stories: <AutoAwesomeMosaicIcon />,
    Artifacts: <MailIcon />,
    Deleted: <DeleteIcon />,
    Popular: <FavoriteIcon />,
    Recent: <TourIcon />,
    Events: (
      <Badge
        color="error"
        variant={selected === "Events" ? "" : "dot"}
        invisible={!todayEvents}
        badgeContent={`${todayEvents}`}
      >
        <ScheduleIcon />
      </Badge>
    ),
    Graph: <ShareIcon />,
    Vitals: <InsightsIcon />,
    Settings: <SettingsIcon />,
    People: <PeopleAltIcon />,
  };

  useEffect(() => {
    const request = async () => {
      try {
        const response = await http_post("stories", {
          project: project,
          events_today: true,
          local_time: new Date().toString(),
        });
        setTodayEvents(response.data.events_today);
      } catch (err) {}
    };

    if (project) {
      request();
    }
  }, [project, reloadData]);

  return (
    <List>
      {[
        "Stories",
        "Popular",
        "Recent",
        "Events",
        "Graph",
        "Artifacts",
        "Deleted",
        "People",
        "Vitals",
        "Settings",
      ].map((text, index) => {
        const menuSelected = (text) => {
          setSelected(text);
        };
        return (
          <ListItem key={index} disablePadding>
            <ListItemButton
              sx={{ m: 0, ml: 1, p: 0 }}
              selected={text === selected}
              onClick={() => menuSelected(text)}
              dense
              disableGutters
            >
              <ListItemIcon>{icons[text]}</ListItemIcon>
              <ListItemText
                primary={
                  <Typography fontWeight={"bold"} fontSize={12}>
                    {text}
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
}

const isPinned = (item) => {
  return item.SK2.startsWith("pin:");
};

function RawMessages({ filterId }) {
  const [data, setData] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const timeFilter = useRecoilValue(timeFilterState);
  const reloadData = useRecoilValue(reloadState);
  const project = useRecoilValue(projectPickerState);

  const getData = async (project) => {
    try {
      setLoading(true);
      const response = await http_post("stories", {
        project: project,
        showlog: true,
        period: timeFilter,
      });
      if (!data || data.length !== response.data.showlog.length) {
        if (!filterId) {
          setData(response.data.showlog);
        } else {
          const data = response.data.showlog.filter(
            (item) => item.SK === filterId
          );
          setData(data);
        }
        setError(null);
      }
    } catch (err) {
      log.debug(err);
      if (!data) {
        setError(err.message);
        setData(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (project) {
      console.log("reloaddata, timefilter");
      getData(project);
    }
  }, [reloadData, timeFilter, project]);

  return (
    <Box>
      {isLoading && <div>Loading data...</div>}
      {error && <div>Failed to fetch data </div>}

      {data && (
        <Thread messages={data} allowReplay={true} expand={filterId !== null} />
      )}
    </Box>
  );
}

function StoryCheckbox({ onChecked, reload, initialState, disabled }) {
  const [checked, setChecked] = useState(false);
  const handleChange = (e) => {
    const val = e.target.checked;
    setChecked(val);
    onChecked(val);
  };

  useEffect(() => {
    if (checked) {
      setChecked(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  useEffect(() => {
    setChecked(initialState);
  }, [initialState]);

  return (
    <Checkbox
      size="small"
      checked={checked}
      onChange={handleChange}
      disabled={disabled}
    ></Checkbox>
  );
}

const daysElapsedSinceDate = (date) => {
  const start = Date.parse(date);
  const now = Date.now();
  if (now < start) {
    return 0;
  }
  const days = Math.floor((now - start) / (1000 * 60 * 60 * 24));
  return days;
};

const daysElapsedSince = (ts) => {
  if (!ts || !ts.length) {
    return -1;
  }

  const start = u.timestampToDate(ts);
  const now = Date.now();
  if (now < start) {
    return 0;
  }
  const days = Math.floor((now - start) / (1000 * 60 * 60 * 24));
  return days;
};

const elapsedDaysToString = (days) => {
  if (days === 0) {
    return "now";
  } else if (days < 30) {
    return `${days}d`;
  } else if (days < 365) {
    return `${Math.floor(days / 30)}m`;
  } else {
    return `${Math.floor(days / 365)}m`;
  }
};

function TimelineChips({ item, forRecentStories }) {
  const [created, setCreated] = useState(-1);
  const [updated, setUpdated] = useState(-1);

  useEffect(() => {
    setCreated(daysElapsedSinceDate(getTimestamp(item)));
    let ts =
      !forRecentStories && "st_updated_ts" in item
        ? item.st_updated_ts
        : item.SK2;
    setUpdated(daysElapsedSince(ts));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  const bothChips = () => {
    const insignificant = Math.abs(created - updated) / created < 0.1;
    return (
      !forRecentStories &&
      elapsedDaysToString(created) !== elapsedDaysToString(updated) &&
      !insignificant
    );
  };

  const color = (days) => {
    if (forRecentStories) {
      return undefined;
    }

    let c = undefined;
    if (days < 3) {
      c = "palegreen";
    } else if (days < 7) {
      c = "wheat";
    } else if (days < 30) {
      c = "pink";
    }
    return c;
  };

  return (
    <Stack direction="row" alignItems="center" gap={0.2}>
      {bothChips() && created >= 0 && (
        <Chip
          size="small"
          disabled={true}
          label={"created " + elapsedDaysToString(created)}
        />
      )}

      {updated >= 0 && (
        <Chip
          size="small"
          disabled={true}
          sx={color(updated) ? { background: color(updated) } : {}}
          label={(bothChips() ? "updated " : "") + elapsedDaysToString(updated)}
        />
      )}
    </Stack>
  );
}

function StoryChip({ item }) {
  return (
    <>
      <Box
        sx={{
          whiteSpace: "nowrap",
          p: 0.2,
          pl: 2,
          pr: 2,
          m: 0,
          background: "#e0e0e0",
          borderRadius: "16px",
        }}
      >
        <Stack direction={"row"} alignItems="center">
          <Typography fontSize={8} color="grey">
            STORY-
          </Typography>
          <Typography fontSize={14} color="#909090">
            {Number(item.SK4)}
          </Typography>
        </Stack>
      </Box>
      {/* <Chip
      size="small"
      disabled={true}
      label={u.getStoryKey(item)}
      onClick={() => {
        navigator.clipboard.writeText(u.getStoryKey(item));
      }}
    /> */}
    </>
  );
}

const hasNotification = (item) => {
  return item.st_ui && item.st_ui.notif && item.st_ui.notif.length > 0;
};

const hasNotificationToday = (item) => {
  if (hasNotification(item)) {
    for (let notif_date of item.st_ui.notif) {
      const date = u.parsePythonTimestamp(notif_date);
      if (u.isTodayDate(date)) {
        return date;
      }
    }
  }
  return null;
};

const hasFutureNotification = (item) => {
  const today = u.todayRange();
  if (hasNotification(item)) {
    for (let notif_date of item.st_ui.notif) {
      const date = u.parsePythonTimestamp(notif_date);
      if (date > today[1]) {
        return date;
      }
    }
  }
  return null;
};

function NotificationBell({ item, sx, onDelete }) {
  const color = "disabled";
  const fontSize = "small";
  const props = { color: color, fontSize: fontSize, sx: sx };
  let Icon = null;

  let future = null;
  let past = null;
  let present = null;

  let future_ts = null;
  let past_ts = null;
  let preset_ts = null;

  const today = u.todayRange();
  if (hasNotification(item)) {
    for (let notif_date of item.st_ui.notif) {
      const date = u.parsePythonTimestamp(notif_date);
      if (u.isTodayDate(date)) {
        present = date;
        preset_ts = notif_date;
        break;
      } else if (date > today[1]) {
        if (!future || date < future) {
          future = date;
          future_ts = notif_date;
        }
      } else {
        if (!past || date > past) {
          past = date;
          past_ts = notif_date;
        }
      }
    }
  }

  let tooltip = null;
  let notif_ts = null;
  if (present) {
    Icon = NotificationsActiveIcon;
    tooltip = present.toString();
    notif_ts = preset_ts;
  } else if (future) {
    Icon = NotificationsIcon;
    tooltip = future.toString();
    notif_ts = future_ts;
  } else {
    Icon = NotificationsNoneIcon;
    tooltip = past.toString();
    notif_ts = past_ts;
  }

  const handleDelete = () => {
    if (onDelete) {
      onDelete(notif_ts);
    }
  };

  return (
    <Tooltip
      title={
        <Fragment>
          <Stack direction="row">
            <Typography fontSize={11}>{tooltip}</Typography>
            {notif_ts !== past_ts && (
              <IconButton
                size="small"
                sx={{ color: "white" }}
                onClick={handleDelete}
              >
                <DeleteIcon fontSize="inherit" />
              </IconButton>
            )}
          </Stack>
        </Fragment>
      }
    >
      <Icon {...props} />
    </Tooltip>
  );
}

function StoryCell({
  item,
  onRestore,
  onPin,
  deleted,
  expand,
  collapse,
  mode,
  onUpdateLabels,
  onLabelCreated,
  labelDiff,
  setLabelDiff,
  smoothCollapseAnimation,
}) {
  const [messages, setMessages] = useState(null);
  const [connected, setConnected] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const labelToggle = useRecoilValue(labelToggleState);

  let ref = useRef({});

  const repliesLabel = (item) => {
    const st_len = "st_len" in item ? item.st_len : 0;
    const num_messages = messages ? messages.length : 0;
    const thread_length = Math.max(st_len, num_messages);
    if (thread_length === 2) {
      // acccount for the thread starter
      return "1 reply";
    } else if (thread_length > 2) {
      return `${thread_length - 1} replies`;
    } else {
      return "reply";
    }
  };

  const getData = async (cached, reply) => {
    try {
      const response = await http_post("stories", {
        messages: true,
        connected: true,
        story: item.SK,
      });

      // const ts = u.getCurrentTimestamp();
      // const hardcoded = [
      //   {
      //     SK: ts,
      //     ts: ts,
      //     event_ts: ts,
      //     type: "message",
      //     st_enc: {
      //       ver: 1,
      //       cipher: "+s+mTDEUY0ljyHIZDhlS+e2N9Rfp8KuopVQx",
      //       salt: "XSnaO+WErpo8zeb+vVSP8TApusJVMZVo1r9iRO5OEOk=",
      //       iv: "kKeCgl0OBSBKEMqM",
      //     },
      //   },
      // ];

      const hardcoded = [];

      if (!messages || response.data.messages.length) {
        const newMessages = response.data.messages.reverse().concat(hardcoded);
        const sorted = u.sortMessages(newMessages, true);

        const updateCache = (sorted) => {
          if (activeProject) {
            const db = new PouchDB(activeProject);

            const key = `Thread:${item.PK}:${item.SK}`;
            const doc = { _id: key, messages: sorted };
            db.get(key)
              .then((prev) =>
                db.put({ _rev: prev._rev, ...doc }).catch((e) => {})
              )
              .catch((e) => {
                db.put({ _id: key, message: sorted }).catch((e) => {});
              });
          }
        };

        if (cached) {
          if (
            cached.length !== sorted.length ||
            (cached.length > 0 &&
              cached[cached.length - 1].SK !== sorted[sorted.length - 1].SK)
          ) {
            setMessages(sorted);
            updateCache(sorted);
          } else if (cached.length === sorted.length) {
            for (let i = 0; i < cached.length; ++i) {
              const rhs = cached[i];
              const lhs = sorted[i];
              if (
                (rhs.st_ui && !lhs.st_ui) ||
                (!rhs.st_ui && lhs.st_ui) ||
                (rhs.st_ui &&
                  lhs.st_ui &&
                  !lodash.isEqual(rhs.st_ui, lhs.st_ui)) ||
                (rhs.st_hide && !lhs.st_hide) ||
                (!rhs.st_hide && rhs.st_hide)
              ) {
                setMessages(sorted);
                updateCache(sorted);
                break;
              }
            }
          }
        } else {
          setMessages((prev) => {
            if (!prev || !prev.length) {
              updateCache(sorted);
              if (sorted.length > 0) {
                return sorted;
              } else {
                return prev;
              }
            } else {
              if (reply) {
                const found = prev.findIndex(
                  (e) => e.dummy && e.ts === reply.ts
                );
                if (found !== -1) {
                  let res = [...prev];
                  let dummy = res[found];
                  reply.msg.old_dummy_message = dummy;
                  res[found] = reply.msg;
                  return res;
                }
              } else {
                updateCache(sorted);
                return sorted;
              }
              return prev;
            }
          });
        }
      }
      setConnected(response.data.connected);
    } catch (err) {
      log.debug(err);
    } finally {
    }
  };

  const handleExpand = (item) => {
    const panelExpanded = !expanded;
    setExpanded(panelExpanded);

    if (panelExpanded) {
      if (activeProject) {
        const key = `Thread:${item.PK}:${item.SK}`;
        const db = new PouchDB(activeProject);

        db.get(key)
          .then((doc) => {
            setMessages(doc.messages);
            getData(doc.messages, null);
          })
          .catch((err) => getData(null, null));
      }
    }
  };

  useEffect(() => {
    if (expand !== expanded) {
      handleExpand(item);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expand]);

  useEffect(() => {
    if ("id" in ref.current && ref.current.id !== item.SK) {
      setExpanded(false);
      setMessages(null);
    }
    ref.current.id = item.SK;
  }, [item]);

  useEffect(() => {
    if (expanded) {
      handleExpand(item);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapse]);

  const handleLinkClick = (e) => {
    const url = u.getStoryUrl(item, activeProject);

    if (e.metaKey) {
      e.stopPropagation();
      window.open(url, "_blank", "noreferrer");
    } else if (!e.shiftKey) {
      navigator.clipboard.writeText(url);
    }
  };

  const handleRepliesClick = (e) => {
    if (e.metaKey) {
      return handleLinkClick(e);
    }
    handleExpand(item);
  };

  const handleNewMessage = (msg) => {
    getData(null, msg);
  };

  const handleReload = () => {
    getData(null, null);
  };

  return (
    <Box>
      <MemoMessage item={item} />
      <Box
        sx={{
          "& > :not(style) + :not(style)": {
            ml: 1,
          },
          flexDirection: "row",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          m: 0,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.2}
          m={0}
          component="span"
        >
          {!item.dummy && (
            <Box sx={{ whiteSpace: "nowrap" }} component="span">
              <ButtonLink
                text={repliesLabel(item)}
                onclick={handleRepliesClick}
              />
            </Box>
          )}
          {true && !deleted && (
            <Tooltip title="Copy link to this message, or open in new tab with Meta+click">
              <IconButton size="small" onClick={handleLinkClick}>
                <LinkIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
          {u.isSlackMessage(item) && (
            <Tooltip title="Open in external app">
              <IconButton
                size="small"
                onClick={() => {
                  exitToApp(item);
                }}
              >
                <ExitToAppIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
          {true && !deleted && !item.dummy && (
            <Tooltip title="Pin topic">
              <IconButton
                size="small"
                color={isPinned(item) ? "primary" : "active"}
                onClick={() => {
                  onPin(item);
                }}
              >
                <PushPinIcon fontSize="inherit" color="active" />
              </IconButton>
            </Tooltip>
          )}
          {deleted && (
            <Tooltip title="Restore this story">
              <IconButton
                size="small"
                disabled={false}
                onClick={() => {
                  onRestore(item);
                }}
              >
                <RestoreFromTrashIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
          {"SK4" in item && <StoryChip item={item} />}
          <TimelineChips
            item={item}
            forRecentStories={mode === "recent" || mode === "popular"}
          />
        </Stack>
        <Stack direction="row" sx={{ width: "100%" }} justifyContent="flex-end">
          {!labelToggle && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row-reverse",
                justifyContent: "flex-start",
                width: "100%",
              }}
            >
              <StoryLabels
                item={item}
                onLabelCreated={onLabelCreated}
                onUpdateLabels={onUpdateLabels}
                minimized={true}
                diff={labelDiff}
                setDiff={setLabelDiff}
                disabled={item.dummy}
              />
            </Box>
          )}
          {true && !item.dummy && (
            <StoryCaption item={item} initialValue={item.st_cap || ""} />
          )}
        </Stack>
      </Box>
      {(!smoothCollapseAnimation || expand) && expanded && (
        <Box sx={{ maxWidth: "900px", mb: 4 }}>
          <StoryDetails
            item={item}
            messages={messages}
            setMessages={setMessages}
            connected={connected}
            setConnected={setConnected}
            onNewMessage={handleNewMessage}
            onRequestReload={handleReload}
            mode={mode}
          />
        </Box>
      )}
      {smoothCollapseAnimation && !expand && (
        <Collapse in={expanded} timeout="auto">
          {expanded && (
            <Box sx={{ maxWidth: "900px", mb: 4 }}>
              <StoryDetails
                item={item}
                messages={messages}
                setMessages={setMessages}
                connected={connected}
                setConnected={setConnected}
                onNewMessage={handleNewMessage}
                onRequestReload={handleReload}
                mode={mode}
              />
            </Box>
          )}
        </Collapse>
      )}
    </Box>
  );
}

function StoryRow({
  item,
  onPin,
  onDelete,
  onSelected,
  onRestore,
  onUpdateLabels,
  collapseAll,
  onLabelCreated,
  mode,
  selectedRow,
  smoothCollapseAnimation,
}) {
  const [selected, setSelected] = useState(false);
  const labelToggle = useRecoilValue(labelToggleState);
  const [labelDiff, setLabelDiff] = useState({});
  const [notificationDeleted, setNotificationDeleted] = useState(false);

  const handleClick = (e) => {
    if (e.metaKey || e.shiftKey) {
      if (e.target.href) {
        return;
      }
      window.getSelection().removeAllRanges();
      setSelected(!selected);
      if (onSelected) {
        onSelected(e, item.SK, !selected);
      }
    }
  };

  const handleCheckbox = (story, checked) => {
    setSelected(checked);
    if (onSelected) {
      onSelected(null, story, checked);
    }
  };

  const handleDeleteNotification = (notif_ts) => {
    const request = async () => {
      try {
        http_post("stories", {
          clearschedule: true,
          story: item.SK,
          notification: notif_ts,
        });
        setNotificationDeleted(true);
      } catch (e) {}
    };
    request();
  };

  const getItemStyle = (item) => ({
    background: getRowColor(item),
  });

  const getRowColor = (item) => {
    if (hasNotificationToday(item)) {
      return "#f5e0e0";
    }

    const colors = {
      day: "#e8e8e8",
      two: "#f1f1f1",
      three: "#f1f1f1",
      week: "#f8f8f8",
      other: "white",
    };
    if (selected) {
      return "#d9e5ed";
    }

    if (isPinned(item)) {
      return "#dadada";
    }

    const ts = item.SK2;
    const start = u.timestampToDate(ts);
    const now = Date.now();
    if (now < start) {
      return colors.day;
    }

    const days = (now - start) / (1000 * 60 * 60 * 24);
    if (days < 1) {
      return colors.day;
    }

    if (days < 2) {
      return colors.two;
    }

    if (days < 4) {
      return colors.three;
    }

    if (days < 7) {
      return colors.week;
    }

    return colors.other;
  };

  useEffect(() => {
    setSelected(selectedRow);
  }, [item]);

  useEffect(() => {
    setSelected(selectedRow);
  }, [selectedRow]);

  return (
    <TableRow
      style={getItemStyle(item)}
      hover={true}
      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
      onClick={(e) => {
        handleClick(e);
      }}
    >
      {true && mode !== "deleted" && (
        <TableCell
          width={1}
          component="th"
          scope="row"
          size="small"
          align="right"
          style={{ verticalAlign: "top" }}
          sx={{ p: 0 }}
        >
          <StoryCheckbox
            initialState={selected}
            onChecked={(checked) => {
              handleCheckbox(item.SK, checked);
            }}
            disabled={item.dummy}
          />
        </TableCell>
      )}

      {true && (
        <TableCell
          component="th"
          scope="row"
          align="left"
          size="small"
          sx={{
            wordBreak: "break-word",
            whiteSpace: "normal",
            verticalAlign: "top",
            pb: 0,
            pl: mode === "deleted" ? 2 : 0,
            pr: 0,
          }}
        >
          <Stack direction="row" gap={1} sx={{ width: "100%" }}>
            {!notificationDeleted && hasNotification(item) && (
              <NotificationBell
                item={item}
                sx={{ mt: 0.5 }}
                onDelete={handleDeleteNotification}
              />
            )}
            <Box sx={{ width: "100%" }}>
              <StoryCell
                item={item}
                expand={mode === "onestory"}
                collapse={collapseAll}
                onPin={onPin}
                onRestore={onRestore}
                deleted={mode === "deleted"}
                mode={mode}
                onUpdateLabels={onUpdateLabels}
                onLabelCreated={onLabelCreated}
                labelDiff={labelDiff}
                setLabelDiff={setLabelDiff}
                smoothCollapseAnimation={smoothCollapseAnimation}
              />
            </Box>
          </Stack>
        </TableCell>
      )}

      {labelToggle && mode !== "deleted" && (
        <TableCell
          width="20%"
          component="th"
          scope="row"
          size="small"
          align="left"
          style={{ verticalAlign: "top" }}
          sx={{ p: 0, pt: 0.2 }}
        >
          <StoryLabels
            item={item}
            onLabelCreated={onLabelCreated}
            onUpdateLabels={onUpdateLabels}
            diff={labelDiff}
            setDiff={setLabelDiff}
            disabled={item.dummy}
          />
        </TableCell>
      )}

      {true && mode !== "deleted" && (
        <TableCell
          width={1}
          component="th"
          scope="row"
          size="small"
          align="left"
          style={{ verticalAlign: "top" }}
          sx={{ pr: 0.5, pt: 0, pb: 0, pl: labelToggle ? 0 : 2 }}
        >
          {true && !item.dummy && (
            <Tooltip title="Delete story (not permanently)">
              <IconButton
                size="small"
                onClick={() => {
                  onDelete(item);
                }}
              >
                <DeleteOutlineIcon color="silver" fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}

const MemoStoryRow = memo(StoryRow);
const MemoShowStories = memo(ShowStories);
const MemoFetchStories = memo(FetchStories);

function StoryDeletedToast({ item, onRestore }) {
  let story = u.getStoryKey(item);
  if (item.st_cap) {
    story = `[${item.st_cap}]`;
  }

  const handleClick = () => {
    if (onRestore) {
      onRestore(item);
    }
  };

  return (
    <Stack direction={"row"} alignItems={"center"}>
      <Typography fontSize={12}>{story} has been deleted</Typography>
      <Button onClick={handleClick} size="small">
        UNDO
      </Button>
    </Stack>
  );
}
export function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

function FetchStories({
  collapseAll,
  filterId,
  timestampRef,
  deleted,
  popular,
  recent,
  events,
  onLabelCreated,
}) {
  const [data, setData] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filtersInUse, setFiltersInUse] = useState(null);
  const filters = useRecoilValue(storyFilterBoxState);
  const [captions, setCaptions] = useRecoilState(captionsState);
  const timeFilter = useRecoilValue(timeFilterState);
  const negateLabels = useRecoilValue(negateLabelsState);
  const [newStories, setNewStories] = useRecoilState(newStoriesState);
  const [mergedStories, setMergedStories] = useRecoilState(mergedStoriesState);
  const [search, setSearch] = useRecoilState(searchState);
  const [lastKeyOlder, setLastKeyOlder] = useState(null);
  const [lastKeyNewer, setLastKeyNewer] = useState(null);
  const [now, setNow] = useState(u.getCurrentTimestamp());
  const [numPeriods, setNumPeriods] = useState(0);
  const [dataSource, setDataSource] = useState(null);
  const [showSelectedStories, setShowSelectedStories] = useRecoilState(
    showSelectedStoriesState
  );
  const selectedStories = useRecoilValue(joinStoriesState);
  const reloadData = useRecoilValue(reloadState);
  const project = useRecoilValue(projectPickerState);
  const previousNegate = usePrevious(negateLabels);

  console.log(`fetch stories. prev data ${data ? data.length : null}`);

  const getProjectFilters = () => {
    return filters && activeProject in filters ? filters[activeProject] : [];
  };

  const processReceivedData = (request, res, source) => {
    console.log("processReceivedData start");
    if (request.dir === "forward") {
      res.sort((a, b) => a["SK2"] < b["SK2"]);
    }

    console.log(`setting data ${res.length}`);
    setData(res);
    setDataSource(source);

    const proj = getActiveProject();
    if (res && proj) {
      // see if there are changes in captions
      let changed = true;
      if (captions && proj in captions) {
        changed = false;
        for (let d of res) {
          const cap = d.st_cap;
          if (cap) {
            if (d.SK !== captions[proj][cap]) {
              changed = true;
              break;
            }
          }
        }
      }

      if (changed) {
        // there are changes in captions and we want to update the states
        let caps = captions ? { ...captions } : {};
        if (!(proj in caps)) {
          caps[proj] = {};
        } else {
          caps[proj] = { ...captions[proj] };
        }

        for (let d of res) {
          if (d.st_cap) {
            caps[proj][d.st_cap] = d.SK;
          }
        }
        store("Captions", JSON.stringify(caps));
        setCaptions(caps);
      }
    }

    console.log("processReceivedData done");
  };

  const fetchDataPyApi = async (request, requestFilters) => {
    console.log("fetchDataPyApi");
    http_post("stories", request)
      .then((response) => {
        let res = response.data;
        let source = null;
        if (request.deleted) {
          res = res.deleted;
          source = "deleted";
        } else if (request.popular) {
          res = res.popular;
          source = "popular";
        } else if (request.recent) {
          res = res.recent;
          source = "recent";
        } else if (request.labeled) {
          res = res.labeled;
          source = "labeled";
        } else if (request.stories) {
          res = res.stories;
          source = "stories";
        } else if (request.search) {
          res = res.search;
          source = "search";
        } else if (request.events) {
          res = res.events;
          source = "events";
        } else {
          res = null;
        }

        console.log(`fetched from ${source} : ${res ? res.length : -1}`);

        if (u.equalArrays(getProjectFilters(), requestFilters)) {
          console.log("processing");
          processReceivedData(request, res, source);
        } else {
          log.warn(
            `skip reload because of mismatching filters ${getProjectFilters()} - ${requestFilters}`
          );
        }

        setFiltersInUse(requestFilters);
        setError(null);
        setLoading(false);
      })
      .catch((err) => {
        log.debug(err);

        if (!data) {
          setError(err.message);
          setData(null);
        }
        setLoading(false);
      });
  };

  const fetchData = async (request, requestFilters) => {
    console.log("fetchData");
    if (
      (true &&
        request.stories &&
        !request.story &&
        !request.direction &&
        !request.start &&
        !request.base_time) ||
      (request.labeled && !request.negate && request.label.length === 1)
    ) {
      http_post("query", request)
        .then((response) => {
          if (request.stories) {
            console.log(`queried storires ${response.data.stories.length}`);
            processReceivedData(request, response.data.stories, "stories");
          } else if (request.labeled) {
            console.log(`queried labeled ${response.data.labeled.length}`);
            processReceivedData(request, response.data.labeled, "labeled");
          }

          setFiltersInUse(requestFilters);
          setError(null);
          setLoading(false);
        })
        .catch((e) => {
          // fallback to the old and slow api
          fetchDataPyApi(request, requestFilters);
        });
    } else {
      fetchDataPyApi(request, requestFilters);
    }
  };

  const getData = async (
    project,
    requestFilters,
    key = null,
    direction = null,
    baseTime = null,
    timePeriod = null
  ) => {
    let request = { project: project };

    if (key) {
      request.start = key;
    }
    if (direction && direction === "forward") {
      request.dir = direction;
    }

    if (baseTime) {
      request.base_time = baseTime;
    }

    if (filterId) {
      request = { ...request, stories: true, story: filterId };
      if (timestampRef) {
        request.timeref = timestampRef;
      }
    } else if (deleted) {
      request = { ...request, deleted: true };
    } else if (popular) {
      request = { ...request, popular: true, label: requestFilters };
    } else if (recent) {
      request = { ...request, recent: true, label: requestFilters };
    } else if (events) {
      request = {
        ...request,
        events: true,
        label: requestFilters,
        local_time: new Date().toString(),
      };
    } else if (requestFilters && requestFilters.length) {
      request = { ...request, labeled: true, label: requestFilters };
    } else {
      request = { ...request, stories: true };
    }
    if (timePeriod) {
      request.period = timePeriod;
    } else {
      request.period = timeFilter;
    }
    request.negate = negateLabels;
    fetchData(request, requestFilters);
  };

  const initiateDataUpdate = (
    project,
    key = null,
    direction = null,
    baseTime = null,
    timePeriod = null
  ) => {
    if (project) {
      setLoading(true);
      const filters = getProjectFilters();
      getData(
        project,
        filters ? [...filters] : null,
        key,
        direction,
        baseTime,
        timePeriod
      );
    }
  };

  const updateContent = (project) => {
    setNumPeriods(0);
    setNow(u.getCurrentTimestamp());
    initiateDataUpdate(project);
  };

  useEffect(() => {
    // optimization: do not reload content if no filters selected and negate flag changes
    if (
      previousNegate !== undefined &&
      previousNegate !== negateLabels &&
      getProjectFilters().length === 0
    ) {
      return;
    }

    if (project && filters && !events && !deleted) {
      updateContent(project);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, reloadData, timeFilter, project, negateLabels]);

  useEffect(() => {
    if (project && (events || deleted)) {
      updateContent(project);
    }
  }, [project]);

  useEffect(() => {
    if (search) {
      setSearch("");
      const request = { search: true, query: search };
      const filters = getProjectFilters();
      fetchData(request, filters ? [...filters] : null);
    }
  }, [search]);

  useEffect(() => {
    if (!mergedStories || mergedStories.length < 2) {
      return;
    }

    const mainStory = mergedStories[0];
    const merged = new Set();
    const mergedCount = mergedStories.length - 1;
    for (let s of mergedStories.slice(1)) {
      merged.add(s);
    }

    setData((prev) => {
      if (!prev) {
        return prev;
      } else {
        let next = [];
        for (const s of prev) {
          if (!merged.has(s.SK)) {
            if (s.SK === mainStory) {
              next.push({ ...s, st_len: s.st_len + mergedCount });
            } else {
              next.push(s);
            }
          }
        }
        return next;
      }
    });

    setMergedStories([]);
  }, [mergedStories]);

  useEffect(() => {
    if (!newStories || !newStories.length) {
      return;
    }

    const matchFilters = (story) => {
      const filters = getProjectFilters();
      if (!filters || !filters.length) {
        return true;
      }
      if (!("st_labels" in story) || !story.st_labels.length) {
        return false;
      }

      for (let l of story.st_labels) {
        if (filters.includes(l)) {
          return true;
        }
      }

      return false;
    };

    let filtered = newStories.filter(matchFilters);
    if (filtered.length) {
      setData((prev) => {
        if (!prev) {
          return filtered;
        } else {
          let newData = [];
          let uniq = new Set();
          for (let s of filtered) {
            uniq.add(s.SK);
            const dummy = s.old_dummy_message;
            if (dummy) {
              uniq.add(dummy.SK);
            }

            if (!s.internal_update) {
              newData.push(s);
            } else {
              const search = s.old_dummy_message.SK;

              // include the updated item only if the original is still there
              const found = prev.find(
                (e) =>
                  e.SK === search ||
                  (e.old_dummy_message && e.old_dummy_message.SK === search)
              );

              if (found) {
                newData.push(s);
              }
            }
          }

          // remove duplicates which may be created as a result of story merging
          for (let s of prev) {
            if (!uniq.has(s.SK)) {
              newData.push(s);
              uniq.add(s.SK);
            }
          }

          return newData;
        }
      });
    }

    setNewStories([]);
  }, [newStories]);

  useEffect(() => {
    if (showSelectedStories) {
      const selected = getCheckedThreads(selectedStories);
      setShowSelectedStories(false);

      if (selected.length > 0) {
        const request = { stories: true, story: selected };
        http_post("stories", request)
          .then((response) => {
            let res = response.data.stories;
            let source = "stories";
            processReceivedData(request, res, source);
            setError(null);
            setLoading(false);
          })
          .catch((err) => {});
      }
    }
  }, [showSelectedStories]);

  useEffect(() => {
    console.log(`data has updated indeed: ${data ? data.length : "null"}`);
  }, [data]);

  const handleDelete = useCallback(
    (item) => {
      const deleteTopic = async (item) => {
        try {
          http_post("label", { delete: { stories: [item["SK"]] } });
        } catch (err) {
        } finally {
        }
      };

      const handleUndo = (item) => {
        const request = async (item) => {
          try {
            http_post("label", { restore: { stories: [item["SK"]] } });
          } catch (err) {
          } finally {
          }
        };

        request(item);
        setData((prev) => {
          const dupe = prev.findIndex((e) => e.SK === item.SK);
          if (dupe === -1) {
            let found = prev.findIndex((e) => e.SK2 < item.SK2);
            if (found === -1) {
              found = prev.length;
            }
            const newData = [...prev];
            newData.splice(found, 0, item);
            return newData;
          }
          return prev;
        });
      };

      deleteTopic(item);
      setData((prev) => {
        const found = prev.findIndex((e) => e.SK === item.SK);
        if (found !== -1) {
          const newData = [...prev];
          newData.splice(found, 1);
          setData(newData);

          toast.warning(
            <StoryDeletedToast
              item={item}
              onRestore={(item) => handleUndo(item)}
            />
          );
          return newData;
        }
        return prev;
      });
    },
    [setData]
  );

  const pinItem = (item, pin) => {
    if (pin) {
      setData((prev) => {
        const index = prev.findIndex((e) => e.SK === item.SK);
        if (index !== -1) {
          const result = Array.from(prev);

          let [removed] = result.splice(index, 1);
          let edited = { ...removed };
          edited.ts_updated_ts = edited.SK2;
          edited.SK2 = "pin:0";

          result.splice(0, 0, edited);
          return result;
        }
        return prev;
      });
    } else {
      setData((prev) => {
        const index = prev.findIndex((e) => e.SK === item.SK);
        if (index !== -1) {
          const result = Array.from(prev);
          let [removed] = result.splice(index, 1);
          let edited = { ...removed };

          const ts =
            "ts_updated_ts" in edited ? edited.ts_updated_ts : edited.SK3;
          edited.SK2 = ts;

          let i = 0;
          while (i < result.length && isPinned(result[i])) {
            i = i + 1;
          }

          while (i < result.length && result[i].SK2 > removed.SK2) {
            i = i + 1;
          }

          result.splice(i, 0, edited);
          return result;
        }
        return prev;
      });
    }
  };

  const handlePin = useCallback(
    (item, index) => {
      const request = async (pin) => {
        try {
          http_post("label", { pin: { topic: item.SK, pin: pin } });
        } catch (err) {
        } finally {
        }
      };

      const pin = !isPinned(item);
      request(pin);
      pinItem(item, pin);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setData]
  );

  const handleRestore = useCallback(
    (item) => {
      const request = async (item) => {
        try {
          http_post("label", { restore: { stories: [item["SK"]] } });
        } catch (err) {
        } finally {
        }
      };
      setData((prev) => {
        return prev.filter((x) => x["SK"] !== item["SK"]);
      });

      request(item);
    },
    [setData]
  );

  const handleUpdatedStoryLabels = useCallback(
    (story, newLabels) => {
      if (dataSource === "search") {
        return;
      }

      if (0 === filtersInUse.length && !negateLabels) {
        return;
      }

      var intersection = filtersInUse.filter(function (n) {
        return newLabels.indexOf(n) > -1;
      });

      const removeFromList = (prev) => {
        const found = prev.findIndex((x) => x.SK === story);
        if (found >= 0) {
          const result = Array.from(prev);
          result.splice(found, 1);
          return result;
        }
        return prev;
      };

      if (
        (!intersection.length && !negateLabels) ||
        (negateLabels && intersection.length)
      ) {
        setData(removeFromList);
        setNewStories(removeFromList);
      }
    },
    [setData, setNewStories, filtersInUse, negateLabels]
  );

  const handleNewLabel = useCallback(
    (label) => {
      onLabelCreated(label);
    },
    [onLabelCreated]
  );

  const getMode = () => {
    if (filterId && data && data.length === 1) {
      return "onestory";
    } else if (popular) {
      return "popular";
    } else if (recent) {
      return "recent";
    } else if (deleted) {
      return "deleted";
    } else if (events) {
      return "events";
    }

    return "regular";
  };

  const handlePageOlder = () => {
    const proj = project || activeProject;
    if (events) {
      initiateDataUpdate(proj, null);
    } else {
      const days = u.timePeriodToDays(timeFilter);

      if (days === -1) {
        let key = null;

        if (data && data.length > 0) {
          const last = data[data.length - 1];
          const pk2 = last.PK.slice(0, -2) + "#o";
          key = { PK: last.PK, SK: last.SK, SK2: last.SK2, PK2: pk2 };
          initiateDataUpdate(proj, key);
          setLastKeyOlder(key);
          setLastKeyNewer(null);
        } else if (lastKeyNewer) {
          initiateDataUpdate(proj);
        }
      } else {
        const baseTime = (
          parseFloat(now) -
          (1 + numPeriods) * days * 24 * 60 * 60
        ).toString();
        setNumPeriods((prev) => prev + 1);
        initiateDataUpdate(proj, null, null, baseTime);
      }
    }
  };

  const handlePageNewer = () => {
    const proj = project || activeProject;
    if (events) {
      initiateDataUpdate(proj, null, "forward");
    } else {
      const days = u.timePeriodToDays(timeFilter);

      if (days === -1) {
        let key = null;

        if (data && data.length > 0) {
          const last = data[0];
          const pk2 = last.PK.slice(0, -2) + "#o";
          key = { PK: last.PK, SK: last.SK, SK2: last.SK2, PK2: pk2 };
          initiateDataUpdate(proj, key, "forward");
          setLastKeyNewer(key);
          setLastKeyOlder(null);
        } else if (lastKeyOlder) {
          initiateDataUpdate(proj, null, "forward");
        }
      } else {
        if (numPeriods > 0) {
          const baseTime = (
            parseFloat(now) -
            (numPeriods - 1) * days * 24 * 60 * 60
          ).toString();
          setNumPeriods((prev) => prev - 1);
          initiateDataUpdate(proj, null, null, baseTime);
        }
      }
    }
  };

  const handleMore = () => {
    const days = u.timePeriodToDays(timeFilter);

    if (days !== -1) {
      setNumPeriods((prev) => prev + 1);
      initiateDataUpdate(
        project || activeProject,
        null,
        null,
        null,
        (days * (numPeriods + 2)).toString() + "d"
      );
    }
  };

  const showPaginationButtons = () => {
    return (
      events ||
      lastKeyNewer ||
      lastKeyOlder ||
      (data && data.length > 0 && getProjectFilters().length === 0)
    );
  };

  return (
    <div>
      <Stack direction="column" gap={1}>
        {isLoading && <div>Loading ...</div>}
        {error && <div>Failed to fetch data </div>}
        <MemoShowStories
          data={data}
          onPin={handlePin}
          onDelete={handleDelete}
          onRestore={handleRestore}
          onUpdateLabels={handleUpdatedStoryLabels}
          collapseAll={collapseAll}
          mode={getMode()}
          onLabelCreated={handleNewLabel}
        />
        <Stack direction={"row"} justifyContent={"center"} gap={2}>
          {showPaginationButtons() && (
            <>
              <ButtonLink
                text={events ? "<< future" : "<< newer"}
                fontSize={11}
                onclick={handlePageNewer}
                disabled={!data || (data.length === 0 && lastKeyNewer)}
              ></ButtonLink>
              <ButtonLink
                text={events ? "past >>" : "older >>"}
                fontSize={11}
                onclick={handlePageOlder}
                disabled={!data || (data.length === 0 && lastKeyOlder)}
              ></ButtonLink>
            </>
          )}
          {!showPaginationButtons() && timeFilter && (
            <ButtonLink
              text="show more"
              fontSize={11}
              onclick={handleMore}
            ></ButtonLink>
          )}
        </Stack>
      </Stack>
    </div>
  );
}
function ShowStories({
  data,
  onPin,
  onDelete,
  onRestore,
  onUpdateLabels,
  collapseAll,
  onLabelCreated,
  mode,
}) {
  console.log(`show stories ${data ? data.length : null}`);

  const [selectedRange, setSelectedRange] = useState();
  let ref = useRef(null); // store last selected index
  const [selected, setSelected] = useRecoilState(joinStoriesState);

  useEffect(() => {
    ref.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (selectedRange && data) {
      let first = data.findIndex((e) => e.SK === selectedRange[0]);
      let last = data.findIndex((e) => e.SK === selectedRange[1]);
      if (first !== -1 && last !== -1) {
        setSelected((prev) => {
          let newSelected = { ...prev };
          for (
            let i = Math.min(first, last) + 1;
            i < Math.max(first, last);
            ++i
          ) {
            if (!(data[i].SK in prev) || !prev[data[i].SK]) {
              newSelected[data[i].SK] = true;
            }
          }
          return newSelected;
        });
      }
      setSelectedRange(null);
    }
  }, [data, selectedRange, setSelected]);

  const handleSelected = useCallback(
    (e, story_id, storySelected) => {
      setSelected((prev) => {
        let newSelected = { ...prev, [story_id]: storySelected };
        if (storySelected && e && e.shiftKey && ref.current) {
          setSelectedRange([story_id, ref.current]);
          ref.current = story_id;
        } else if (storySelected) {
          ref.current = story_id;
        } else {
          ref.current = null;
        }
        return newSelected;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSelected, setSelectedRange]
  );

  const handleDelete = useCallback(
    (item) => {
      if (onDelete) {
        onDelete(item);
      }
    },
    [onDelete]
  );

  return (
    <Stack direction="column">
      {data && data.length > 1 && (
        <>
          <Divider />
          <Typography
            fontSize={8}
            fontFamily={"monospace"}
            sx={{ color: "silver" }}
          >
            {`${data.length} stories displayed`}
          </Typography>
        </>
      )}
      <TableContainer component={Paper}>
        <Table sx={{}} size="small" aria-label="simple table">
          <TableBody>
            {data &&
              data.map((item, i) => (
                <MemoStoryRow
                  key={
                    item.old_dummy_message ? item.old_dummy_message.SK : item.SK
                  }
                  item={item}
                  onSelected={handleSelected}
                  onPin={onPin}
                  onDelete={handleDelete}
                  onRestore={onRestore}
                  onUpdateLabels={onUpdateLabels}
                  collapseAll={collapseAll}
                  deleted={mode === "deleted"}
                  onLabelCreated={onLabelCreated}
                  mode={mode}
                  selectedRow={item.SK in selected && selected[item.SK]}
                  smoothCollapseAnimation={i < 30}
                />
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

function AuthStage({ stage }) {
  switchStage(stage);

  return (
    <Authenticator>
      {({ signOut, user }) => {
        return (
          <RecoilRoot>
            <Home signOut={signOut} />
          </RecoilRoot>
        );
      }}
    </Authenticator>
  );
}

function useQuery() {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
}

function AppRoutes() {
  let query = useQuery();
  const getStage = () => {
    return query.get("stage") || "prod2";
  };

  return (
    <Routes>
      <Route path="/" element={<AuthStage stage={getStage()} />} />
      <Route
        path=":type/:proj/:id?/:ts?"
        element={<AuthStage stage={getStage()} />}
      />
      <Route path=":proj" element={<AuthStage stage={getStage()} />} />
    </Routes>
  );
}

function App() {
  const [wasmLoaded, setWasmLoaded] = useState(false);
  useEffect(() => {
    init().then((e) => {
      setWasmLoaded(true);
    });
  });

  return (
    <>
      {wasmLoaded && (
        <Router>
          <AppRoutes />
        </Router>
      )}
    </>
  );
}

function ConfigLine({ initKey, initValue, onEdit, editable }) {
  const [editKey, setEditKey] = useState(false);
  const [editValue, setEditValue] = useState(false);
  const [key, setKey] = useState(initKey);
  const [value, setValue] = useState(initValue);
  const [focusValue, setFocusValue] = useState(true);

  const handleEditKey = () => {
    setEditKey(true);
    setEditValue(false);
  };

  const handleEditValue = () => {
    setEditValue(true);
    setEditKey(false);
  };

  const handleEdit = (e, field) => {
    if (e.key === "Enter") {
      e.preventDefault();

      let currentKey = key;
      let currentValue = value;
      if (field === "key") {
        currentKey = e.target.value;
        setKey(currentKey);
        setEditKey(false);
        if (currentKey) {
          setFocusValue(true);
          setEditValue(true);
        }
      } else if (field === "value") {
        currentValue = e.target.value;
        setValue(currentValue);
        setFocusValue(false);
        setEditValue(false);
      }

      if (
        currentKey &&
        (currentKey !== initKey || currentValue !== initValue)
      ) {
        onEdit(currentKey, currentValue);
      }
    }
  };

  const handleBlur = (e, field) => {
    let currentKey = key;
    let currentValue = value;
    if (field === "key") {
      currentKey = e.target.value;
      setKey(currentKey);
      setEditKey(false);
    } else if (field === "value") {
      currentValue = e.target.value;
      setValue(currentValue);
      setEditValue(false);
    }

    if (currentKey && (currentKey !== initKey || currentValue !== initValue)) {
      onEdit(currentKey, currentValue);
    }
  };

  return (
    <TableRow
      key={"_add_new_project_configuration_"}
      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
    >
      <TableCell width="25%" component="th" scope="row">
        {!editKey && (
          <Stack
            direction="row"
            gap={0.5}
            alignItems={"center"}
            justifyContent={!initKey ? "" : "space-between"}
          >
            <Typography fontSize={12}>{key}</Typography>
            {editable && (
              <IconButton size="small" onClick={handleEditKey}>
                <>
                  {(initKey || initValue) && <EditIcon fontSize="inherit" />}
                  {!initKey && !initValue && <AddIcon fontSize="inherit" />}
                </>
              </IconButton>
            )}
          </Stack>
        )}
        {editKey && (
          <TextField
            fullWidth
            autoFocus
            label="Key"
            defaultValue={key}
            variant="outlined"
            onKeyDown={(e) => {
              handleEdit(e, "key");
            }}
            onBlur={(e) => {
              handleBlur(e, "key");
            }}
          />
        )}
      </TableCell>
      <TableCell align="left">
        {!editValue && key && (
          <Stack
            direction="row"
            gap={0.5}
            alignItems={"center"}
            justifyContent={"space-between"}
          >
            <Typography fontSize={12}>{value}</Typography>
            {editable && (
              <IconButton size="small" onClick={handleEditValue}>
                <EditIcon fontSize="inherit" />
              </IconButton>
            )}
          </Stack>
        )}
        {editValue && (
          <TextField
            fullWidth
            autoFocus
            defaultValue={value}
            label="Value"
            variant="outlined"
            focused={focusValue}
            onKeyDown={(e) => {
              handleEdit(e, "value");
            }}
            onBlur={(e) => {
              handleBlur(e, "value");
            }}
          />
        )}
      </TableCell>
    </TableRow>
  );
}

function ProjectConfig() {
  const [config, setConfig] = useState(null);
  const [reloadData] = useRecoilState(reloadState);
  const project = useRecoilValue(projectPickerState);

  const getData = (project) => {
    const request = async () => {
      try {
        const response = await http_post("admin", {
          action: "readconfig",
          project: project,
        });
        let conf = [];
        for (let [k, v] of Object.entries(response.data)) {
          if (k !== "SK" && k !== "PK" && k != "original_owner") {
            conf.push({ name: k, value: v });
          }
        }
        const order = ["owner", "slack", "github", "gitlab"];
        let reordered = [];
        let visited = new Set();
        for (let o of order) {
          for (let c of conf) {
            if (c.name.startsWith(o)) {
              reordered.push(c);
              visited.add(c.name);
            }
          }
        }
        for (let c of conf) {
          if (!visited.has(c.name)) {
            reordered.push(c);
          }
        }

        setConfig(reordered);
      } catch (err) {
        log.debug(err);
      } finally {
      }
    };
    request();
  };

  const handleEdit = (key, value) => {
    const request = async () => {
      try {
        http_post("admin", { action: "config", key: key, value: value });
      } catch (err) {
      } finally {
      }
    };
    request();
  };

  const handleNewConfig = (key, value) => {
    if (key && value) {
      setConfig([...config, { name: key, value: value }]);
      handleEdit(key, value);
    }
  };

  useEffect(() => {
    if (!config) {
      console.log("effect empty config");
      getData(project || getActiveProject());
    }
  });

  useEffect(() => {
    console.log("effect reload data or project");
    getData(project || getActiveProject());
  }, [reloadData, project]);

  return (
    <Stack direction={"column"}>
      <Typography fontWeight={"bold"} sx={{ m: 2 }} fontSize={14}>
        Project key-value settings:
      </Typography>
      <TableContainer component={Box}>
        <Table sx={{}} aria-label="simple table" size="small">
          <TableBody>
            {config &&
              config.map((row, index) => (
                <ConfigLine
                  key={row.name}
                  initKey={row.name}
                  initValue={row.value}
                  editable={row.name !== "owner" && row.name !== "project"}
                  onEdit={handleEdit}
                />
              ))}
            {config && (
              <ConfigLine
                key={config.length}
                initKey=""
                initValue=""
                editable={true}
                onEdit={handleNewConfig}
              />
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

function DangerousZone() {
  const [confirmation, setConfirmation] = useState("");
  const statement = `permanently delete ${activeProject}`;

  const handleDelete = () => {
    const request = async () => {
      try {
        await http_post("admin", { action: "revoke" });
        window.location.href = u.getHomeUrl();
      } catch (err) {}
    };

    if (confirmation === statement) {
      setConfirmation("");
      request();
    }
  };

  const handleChange = (e) => {
    setConfirmation(e.target.value);
  };

  return (
    <Box
      border={1}
      sx={{ p: 2, mt: 2, backgroundColor: "#ffefef" }}
      color="red"
      borderRadius={3}
    >
      <Typography color="red" fontWeight={"bold"} fontSize={14}>
        Dangerous stuff. Tread carefully.
      </Typography>
      <Stack direction={"row"} gap={1} sx={{ mt: 2 }}>
        <TextField
          label={statement}
          value={confirmation}
          onChange={handleChange}
        />
        <Button onClick={handleDelete} disabled={confirmation !== statement}>
          Delete project
        </Button>
      </Stack>
    </Box>
  );
}

function TeamMembers() {
  const [chips, setChips] = useState(null);
  const project = useRecoilValue(projectPickerState);
  const reloadData = useRecoilValue(reloadState);

  const getData = (project) => {
    const request = async () => {
      try {
        const response = await http_post("admin", {
          action: "users",
          project: project,
        });
        setChips(response.data.users);
      } catch (err) {
        log.debug(err);
      }
    };
    request();
  };

  useEffect(() => {
    if (!chips) {
      getData(project || getActiveProject());
    }
  });

  useEffect(() => {
    getData(project || getActiveProject());
  }, [project, reloadData]);

  const handleChange = (newChips) => {
    const request = async (action, email) => {
      try {
        http_post("admin", { action: action, project: project, email: email });
      } catch (err) {}
    };
    for (let email of newChips) {
      if (!chips.includes(email)) {
        request("grant", email);
      }
    }
    for (let email of chips) {
      if (!newChips.includes(email)) {
        request("revoke", email);
      }
    }
    setChips(newChips);
  };

  return (
    <Box border={1} sx={{ p: 2 }} borderRadius={3}>
      <Typography
        fontWeight={"bold"}
        sx={{ mb: 2 }}
        fontSize={DEFAULT_FONT_SIZE}
      >
        Manage your team, invite or remove people. Enter an email.
      </Typography>
      <MuiChipsInput value={chips || []} onChange={handleChange} />
    </Box>
  );
}

function Workflow() {
  const project = useRecoilValue(projectPickerState);
  const [workflow, setWorkflow] = useState(null);
  const [error, setError] = useState(false);
  const activeLabels = useRecoilValue(activeLabelsState);
  const [dirty, setDirty] = useState(false);
  const [focused, setFocused] = useState(false);

  const getData = () => {
    const request = async () => {
      try {
        const response = await http_post("admin", {
          action: "getworkflow",
          project: project,
        });
        setWorkflow(JSON.stringify(response.data.workflow, null, 2));
        setError(false);
      } catch (err) {
        log.debug(err);
      }
    };
    request();
  };

  useEffect(() => {
    if (!workflow) {
      getData();
    }
  });

  useEffect(() => {
    getData();
  }, [project]);

  const handleChange = (e) => {
    setWorkflow(e.target.value || " ");
    setDirty(true);
  };

  const handleSave = () => {
    const request = async (workflow) => {
      try {
        http_post("admin", {
          action: "setworkflow",
          project: project,
          workflow: workflow,
        });
      } catch (err) {}
    };

    try {
      let parsed = JSON.parse('{"states":{}}');
      if (workflow.trim()) {
        parsed = JSON.parse(workflow);
      }

      let display = {};
      for (let [k, v] of Object.entries(activeLabels)) {
        display[v.display] = k;
      }

      let fixedWorkflow = { states: {} };
      for (let [k, v] of Object.entries(parsed.states)) {
        let fixed = [];
        for (let label of v) {
          if (label in display && !(label in activeLabels)) {
            fixed.push(display[label]);
          } else {
            fixed.push(label);
          }
        }
        fixedWorkflow.states[k] = fixed;
      }

      setWorkflow(JSON.stringify(fixedWorkflow, null, 2));
      request(fixedWorkflow);
      setError(false);
      setDirty(false);
    } catch (e) {
      setError(true);
    }
  };

  const handleCancel = () => {
    getData();
    setDirty(false);
  };

  return (
    <Box border={0} sx={{ m: 0, p: 2 }}>
      <TextField
        error={error}
        width="100%"
        size="small"
        value={workflow || " "}
        fullWidth
        multiline
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        variant="outlined"
        label="Default Workflow"
        inputProps={{
          style: {
            fontSize: 12,
            fontFamily: "monospace",
            lineHeight: 1.5,
            color: focused ? "black" : "#707070",
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Stack direction={"row"}>
                <Button onClick={handleSave} disabled={!dirty}>
                  Save
                </Button>
                <Button onClick={handleCancel} disabled={!dirty}>
                  Cancel
                </Button>
              </Stack>
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
}

function Settings() {
  const [denied, setDenied] = useState("unknown");
  const project = useRecoilValue(projectPickerState);
  const reloadData = useRecoilValue(reloadState);

  const getData = (project) => {
    const request = async () => {
      try {
        await http_post("admin", { action: "users", project: project });
        setDenied("allowed");
      } catch (err) {
        setDenied("denied");
      }
    };

    if (project) {
      request();
    }
  };

  useEffect(() => {
    if (denied === "unknown") {
      getData(project || getActiveProject());
    }
  });

  useEffect(() => {
    if (project) {
      getData(project);
    }
  }, [project, reloadData]);

  const getHookUrl = () => {
    const proj = project || getActiveProject();
    return hookUrl + proj;
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(getHookUrl());
  };

  return (
    <>
      {denied === "allowed" && (
        <Stack alignItems={"center"}>
          <Stack width="70%" direction="column">
            <Box border={1} sx={{ mt: 2, mb: 2, p: 2 }} borderRadius={3}>
              <Typography fontSize={9}>Your webhook URL:</Typography>
              <Stack direction={"row"} alignItems={"center"}>
                <Typography>{getHookUrl()}</Typography>
                <IconButton size="small" sx={{ pl: 1 }} onClick={handleCopyUrl}>
                  <ContentCopyIcon fontSize="inherit" />
                </IconButton>
              </Stack>
            </Box>
            <Stack border={1} borderRadius={3} gap={5} sx={{ p: 2 }}>
              <ProjectConfig />
              <Workflow />
            </Stack>
            <Box sx={{ m: 1 }}></Box>
            <TeamMembers />
            <DangerousZone />
          </Stack>
        </Stack>
      )}
    </>
  );
}

function PeopleData() {
  const project = useRecoilValue(projectPickerState);
  const setData = useSetRecoilState(peopleDataState);

  const getData = (project) => {
    const request = async () => {
      try {
        const response = await http_post("stories", {
          users: true,
          project: project,
        });
        setData(response.data.users.accounts);
      } catch (err) {
        log.debug(err);
      }
    };
    if (project) {
      request();
    }
  };

  useEffect(() => {
    getData(getActiveProject());
  });

  useEffect(() => {
    getData(project);
  }, [project]);

  return <></>;
}

function People() {
  const data = useRecoilValue(peopleDataState);

  const handleAppClick = (row) => {
    window.open(row["url"], "_blank", "noreferrer");
  };

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 650 }} aria-label="simple table">
        <TableBody>
          {data &&
            data.map((row, index) => (
              <TableRow
                key={row.SK}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                <TableCell
                  component="th"
                  scope="row"
                  width="1%"
                  sx={{ pb: 1, pt: 0.5 }}
                >
                  <Stack direction={"column"} gap={0.5} alignItems="center">
                    <IconButton
                      size="small"
                      disabled={!("url" in row)}
                      onClick={() => handleAppClick(row)}
                    >
                      <Icon icon={`uil:${row.app}`} />
                    </IconButton>
                    {"avatar" in row && (
                      <Box
                        component="img"
                        sx={{ height: 20 }}
                        src={row.avatar}
                      />
                    )}
                  </Stack>
                </TableCell>
                <TableCell
                  width="25%"
                  component="th"
                  scope="row"
                  sx={{ pt: 0.5, pb: 0.5 }}
                >
                  <Stack direction={"row"} gap={2}>
                    <Stack direction={"column"}>
                      {"name" in row && (
                        <Typography fontSize={10}>{row.name}</Typography>
                      )}
                      {"username" in row && (
                        <Typography fontSize={10}>{row.username}</Typography>
                      )}
                      {"email" in row && (
                        <Typography fontSize={10}>{row.email}</Typography>
                      )}
                      <Typography fontSize={10} color="silver">
                        {row.app}
                      </Typography>
                    </Stack>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function TimeFilters() {
  const alltime = String.fromCodePoint(0x221e);
  const filters = ["1d", "2d", "1w", "1m", alltime];
  const [selected, setSelected] = useRecoilState(timeFilterState);

  const handleClick = (e, item) => {
    if (item !== selected) {
      setSelected(item);
    } else if (selected !== "alltime") {
      setSelected(alltime);
    }
  };

  const getColor = (item) => {
    if (item !== selected) {
      return "default";
    } else if (item === alltime) {
      return "success";
    }
    return "primary";
  };

  return (
    <Grid container spacing={1} sx={{ m: 0.5 }}>
      {filters.map((item) => (
        <Grid key={item}>
          <Chip
            size="small"
            label={item}
            color={getColor(item)}
            onClick={(e) => {
              handleClick(e, item);
            }}
          />
        </Grid>
      ))}
    </Grid>
  );
}

export default App;
